import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface PageDeleteInfo {
  childCount: number
  subtreeCount: number
  referenceCount: number
}

interface DeletePageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pageTitle: string
  info: PageDeleteInfo
  deleting: boolean
  onConfirm: (children?: 'delete' | 'promote') => void
}

/**
 * Confirms deletion of a Page. Not built on the shared `ConfirmationDialog`
 * (confirm/cancel only) because a page with subpages needs a THIRD action
 * (promote subpages instead of deleting them) — see `PageDetails.tsx`'s
 * local-dialog pattern (this component doesn't render under `PageContent`,
 * so `useConfirmation()` is unavailable here).
 */
export function DeletePageDialog({
  open,
  onOpenChange,
  pageTitle,
  info,
  deleting,
  onConfirm,
}: DeletePageDialogProps) {
  const { childCount, subtreeCount, referenceCount } = info
  const hasChildren = childCount > 0

  const referenceNote =
    referenceCount > 0
      ? ` This page is linked from ${referenceCount} place(s) (menus, footer, or buttons); those links will be cleared.`
      : ''

  const childrenNote =
    subtreeCount > childCount
      ? `${childCount} subpage(s) (${subtreeCount} pages total in this section)`
      : `${childCount} subpage(s)`

  const description = hasChildren
    ? `This page has ${childrenNote}. Choose whether to delete them too, or promote them so they become children of this page's parent instead — they will be kept, not deleted.${referenceNote}`
    : `This cannot be undone.${referenceNote}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete page &quot;{pageTitle}&quot;?</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {hasChildren ? (
            <>
              <Button
                variant="destructive"
                disabled={deleting}
                onClick={() => onConfirm('delete')}
              >
                Delete all
              </Button>
              <Button
                variant="outline"
                disabled={deleting}
                onClick={() => onConfirm('promote')}
              >
                Promote subpages up
              </Button>
              <Button
                variant="ghost"
                disabled={deleting}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                disabled={deleting}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleting}
                onClick={() => onConfirm()}
              >
                Delete
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
